import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, RefreshControl, StyleSheet } from 'react-native';

import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { RouteProp, useNavigation, useRoute, useScrollToTop } from '@react-navigation/native';

import { HeaderNavTabs } from '@/components/HeaderNavTabs';
import { SizedBox } from '@/components/SizedBox';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { GameResponsibly } from '@/feature/invite-friends/components/GameResponsibly';
import { usePromotions } from '@/feature/promohub/hooks/use-promotions';
import { usePromotionsStore } from '@/feature/promohub/hooks/use-promotions-store';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useResponsiveColumnCount } from '@/feature/responsive-design/hooks/use-responsive-column-count';
import { useAppResumeEffect } from '@/hooks/use-resume';
import { PromohubStackParamList } from '@/navigation/types';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { PromoCard } from '../components/PromoCard';
import { usePromotionsNavigation } from '../hooks/use-promotions-navigation';
import { Promotion } from '../types';

export type PromohubTabParamList = { Available: undefined; 'Opted-in': undefined };

const Tab = createMaterialTopTabNavigator<PromohubTabParamList>();

const ItemSeparator = () => <SizedBox value={16} />;

export const PromohubTopTabBarScreen = () => (
    <MaxWidthWrapper flex={1}>
        <PromohubScreen />
    </MaxWidthWrapper>
);

export type PromohubParams = RouteProp<PromohubStackParamList, 'Promohub'>;

export const PromohubScreen = () => {
    const { params } = useRoute<PromohubParams>();
    const navigation = useNavigation();

    const { promotions, refetch, isLoading, showRgFooter } = usePromotions();
    const { goToPromo: handleGoToPromo } = usePromotionsNavigation();
    const locallyOptedInPromos = usePromotionsStore(state => state.locallyOptedInPromos);
    const clearLocallyOptedInPromos = usePromotionsStore(state => state.actions.clearLocallyOptedIn);
    const goToPromo = useCallback(
        (promoId: string, external_url?: string | null) => {
            handleGoToPromo(promoId, external_url);
            //Clear locally opted in promos so those promos are moved to the Opted-In tab when the user comes back from Promo Details
            clearLocallyOptedInPromos();
        },
        [clearLocallyOptedInPromos, handleGoToPromo]
    );

    const promoTabsListeners = useMemo(() => {
        return {
            state: () => {
                // refetch promotions when the user navigates to a different tab
                clearLocallyOptedInPromos();
                refetch();
            },
        };
    }, [clearLocallyOptedInPromos, refetch]);

    const reload = useCallback(() => {
        clearLocallyOptedInPromos();
        refetch({ force: true });
    }, [clearLocallyOptedInPromos, refetch]);

    const { showToast } = useAlerts();
    const columnCount = useResponsiveColumnCount([1, 1, 1, 2, 3]);

    const { t } = useTranslation(['promohub']);

    useEffect(() => {
        if (params.promoId && !isLoading) {
            const promoData = promotions.find(promo => promo.uid === params.promoId);
            if (promoData) {
                goToPromo(params.promoId, promoData.data.external_url);
            } else {
                showToast({ message: t('promotion_not_valid'), toastType: 'warning' });
            }
            // clear the params so the promotion doesn't open up again if the user navigates back to the promohub
            navigation.setParams({ promoId: undefined });
        }
    }, [goToPromo, isLoading, navigation, params.promoId, promotions, showToast, t]);

    useAppResumeEffect(
        useCallback(() => {
            reload();
        }, [reload])
    );

    const optedInPromotions = promotions.filter(promo => promo.optedIn);
    const availablePromotions = promotions.filter(promo => !promo.optedIn || locallyOptedInPromos.includes(promo.uid));

    return (
        <HeaderNavTabs<PromohubTabParamList>
            title={t('promohub:promos')}
            mode={params.mode}
            initialRouteName={'Available'}
            customTabsListeners={promoTabsListeners}
        >
            <Tab.Screen name={'Available'} key={'Available'} options={{ title: t('promohub:available') }}>
                {() => (
                    <PromotionsList
                        promotions={availablePromotions}
                        isLoading={isLoading}
                        reload={reload}
                        columnCount={columnCount}
                        showRgFooter={showRgFooter}
                        mode={params.mode}
                        testID={'available-promotions-list'}
                    />
                )}
            </Tab.Screen>
            <Tab.Screen name={'Opted-in'} key={'Opted-in'} options={{ title: t('promohub:opted_in') }}>
                {() => (
                    <PromotionsList
                        promotions={optedInPromotions}
                        isLoading={isLoading}
                        reload={reload}
                        columnCount={columnCount}
                        showRgFooter={showRgFooter}
                        mode={params.mode}
                        testID={'opted-in-promotions-list'}
                    />
                )}
            </Tab.Screen>
        </HeaderNavTabs>
    );
};

type PromotionsListTypes = {
    promotions: Promotion[];
    isLoading: boolean;
    reload: () => void;
    columnCount: number;
    showRgFooter?: boolean;
    mode?: 'stack' | 'tabs';
    testID?: string;
};

export type PromoHubRoute = RouteProp<PromohubStackParamList, 'Promohub'>;

export const PromotionsList = ({
    promotions,
    isLoading,
    reload,
    columnCount,
    showRgFooter,
    mode,
    testID,
}: PromotionsListTypes) => {
    const listRef = useRef<FlatList<Promotion>>(null);

    useScrollToTop(listRef);

    const refreshControl = useMemo(() => {
        return (
            <RefreshControl
                enabled
                colors={[ds.colorValue.icon.primary]}
                tintColor={ds.colorValue.icon.primary}
                refreshing={isLoading}
                onRefresh={() => reload()}
            />
        );
    }, [isLoading, reload]);

    return (
        <FlatList
            testID={testID}
            ref={mode === 'tabs' ? listRef : undefined}
            keyExtractor={item => item.uid}
            key={`${columnCount}`}
            scrollEventThrottle={16}
            data={promotions}
            refreshControl={refreshControl}
            numColumns={columnCount}
            style={{ backgroundColor: ds.colorValue.background.neutral.base }}
            columnWrapperStyle={columnCount !== 1 ? styles.columns : undefined}
            contentContainerStyle={styles.contentContainer}
            ItemSeparatorComponent={ItemSeparator}
            showsVerticalScrollIndicator={showScrollIndicator}
            renderItem={({ item }) => <PromoCard key={item.uid} promo={item} mode={'promohub'} />}
            ListFooterComponent={showRgFooter ? <GameResponsibly style={styles.gameResponsibly} /> : null}
        />
    );
};

const styles = StyleSheet.create({
    columns: { gap: 15 },
    contentContainer: {
        paddingTop: 28,
        paddingBottom: 16,
        paddingHorizontal: 16,
    },

    gameResponsibly: {
        paddingTop: 80,
    },
});
